import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Lock } from "lucide-react";
import { Outlet } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { SHELL } from "../../constants/testIds";
import { useAppData } from "../../contexts/AppDataContext";
import { useIdleWarning, IdleWarningBanner } from "../../hooks/useIdleWarning";
import { fadeIn } from "../../lib/motion";
import { EmulationFlag } from "../emulation/EmulationFlag";
import { GymLoader } from "../common/GymLoader";
import { DemoPreviewBanner } from "../trial/DemoPreviewBanner";
import { TrialBlockModal } from "../trial/TrialBlockModal";
import { OnboardingTourOverlay, OnboardingHelpButton } from "../onboarding/OnboardingTour";
import { useOnboardingTour } from "../onboarding/useOnboardingTour";
import { OWNER_STEPS } from "../onboarding/onboardingSteps";

// The shared app shell every P01+ screen mounts into (UX-01 §1 "reused by all later P01
// screens"): sidebar + glass top bar + a permission/read-only-aware content area. UX-CP-03: the
// emulation flag mounts HERE (one place) so every current AND future gym-shell screen inherits
// it automatically — the wrapper contract, not a per-screen change. UX-TRIAL: same "one place,
// every screen inherits it" reasoning for the held-signup demo-preview banner and the global
// trial-cap block modal (driven by api.js's `fitfrik:trial-cap-exceeded` window event).
export function AppShell() {
  const { readOnlyLapse, me, loading } = useAppData();
  const { warning, staySignedIn } = useIdleWarning();
  const reducedMotion = useReducedMotion();
  const emulating = me?.emulation?.active;
  const [capModal, setCapModal] = useState({ open: false, message: null });
  const tour = useOnboardingTour("owner", me?.login_count, me?.id);

  useEffect(() => {
    const handler = (e) => setCapModal({ open: true, message: e.detail?.message });
    window.addEventListener("fitfrik:trial-cap-exceeded", handler);
    return () => window.removeEventListener("fitfrik:trial-cap-exceeded", handler);
  }, []);

  // DEFECT-D-25: matches every other shell (Desk/Trainer/Kairoo/Member) — this one previously
  // rendered Sidebar/TopBar immediately with `me` still null, which is exactly the
  // "inconsistent, sometimes-branded-sometimes-not" loading experience the defect names.
  if (loading) return <GymLoader fullPage label="Loading your gym…" testId={SHELL.appShellLoading} />;

  return (
    <div className="scarlet-canvas flex" style={emulating ? { paddingTop: "var(--emulation-flag-height)" } : undefined}>
      {emulating && <EmulationFlag emulation={me.emulation} orgId={me.organization_id} gymName={me.organization_display_name} />}
      <Sidebar />
      <div className="flex-1 min-w-0 pb-8">
        <TopBar />
        {me?.is_trial_preview && <DemoPreviewBanner />}
        {readOnlyLapse && (
          <motion.div
            data-testid={SHELL.readOnlyBanner}
            {...fadeIn(reducedMotion)}
            className="mx-8 mt-4 rounded-[10px] px-4 py-3 flex items-center gap-2 text-sm font-medium"
            style={{ background: "var(--status-overdue-bg)", color: "var(--status-overdue-text)" }}
          >
            <Lock size={16} />
            This organization's license has lapsed — data is read-only until billing is resolved. Edit actions are disabled.
          </motion.div>
        )}
        <main className="px-8 pt-6" data-testid="app-shell-content" aria-disabled={readOnlyLapse}>
          <Outlet />
        </main>
      </div>
      {warning && <IdleWarningBanner onStaySignedIn={staySignedIn} />}
      <TrialBlockModal
        open={capModal.open}
        onOpenChange={(v) => setCapModal((c) => ({ ...c, open: v }))}
        message={capModal.message}
      />
      {!emulating && (
        <>
          <OnboardingTourOverlay steps={OWNER_STEPS} tour={tour} />
          <OnboardingHelpButton onClick={tour.restart} />
        </>
      )}
    </div>
  );
}
